/**
 * Visão do Plano de Ação para a conta `unidade`. NÃO tem login próprio: o login
 * é único (a tela principal); esta view recebe a sessão (`token`/`nome`/`cargo`)
 * do `useAppState` e é renderizada pela casca quando o papel é `unidade`. A
 * unidade NÃO vê nenhuma outra parte do sistema — só os Planos de Ação da própria
 * unidade (anexar evidência; o time CAINT revisa). A UI dos itens vem de
 * `components/plano/planoCompartilhado` — a MESMA da visão interna, sem divergir.
 *
 * Layout (redesign): topo escuro full-bleed com o conteúdo centralizado em
 * `.pl-topo-in`; corpo em `.pl-shell` (grid de 2 colunas) com uma barra lateral
 * de contexto (`.pl-side`) que orienta o usuário leigo — marca, identidade da
 * unidade, "como funciona", legenda de situações, prazos e ajuda. NENHUM índice
 * de maturidade é exibido (fora do escopo do papel `unidade`). No celular a barra
 * vira um painel "Sobre" recolhível acima do plano.
 */
import { UNIDADES } from "@caint/data";
import { Logo } from "@caint/design-system";
import type { PlanoAcao, PlanoItem, RelatorioDTO } from "@caint/shared";
import { useCallback, useEffect, useMemo, useState } from "react";
import { LegendaMaturidade } from "../components/plano/contextoAvaliacao.tsx";
import { BarraProgresso, SecoesPorSetor, totaisDeItens } from "../components/plano/planoCompartilhado.tsx";
import { useAviso } from "../components/plano/useAviso.ts";
import { RelatorioIndisponivel } from "../components/relatorio/RelatorioIndisponivel.tsx";
import { EmitirRelatorioOverlay } from "../components/relatorio/RelatorioInstitucional.tsx";
import { ApiError, apiClient } from "../lib/api.ts";
import { nomeCargo } from "../lib/planoUnidade.ts";
import "./portal.css";

const NOME_UNIDADE = new Map(UNIDADES.map((u) => [u.id, u.nome]));

export function PortalUnidade({
  token,
  nome,
  cargo,
  onSair,
}: {
  token: string;
  nome: string;
  cargo: string | null;
  onSair: () => void;
}) {
  const [planos, setPlanos] = useState<PlanoAcao[] | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  // Aviso curto ("Plano salvo ✓") após salvar um item; some sozinho.
  const { aviso, notificar } = useAviso();
  // Barra lateral de contexto: recolhida por padrão no celular; sempre visível no
  // desktop (o CSS ignora este estado acima de 860px).
  const [sobreAberto, setSobreAberto] = useState(false);

  // Emissão do Relatório Preliminar (PDF): só o backend decide se está disponível
  // (avaliação VALIDADA). 409 ⇒ ainda não liberado; 401 ⇒ sessão expirada.
  const [relatorio, setRelatorio] = useState<RelatorioDTO | null>(null);
  const [relIndisponivel, setRelIndisponivel] = useState(false);
  const [emitindo, setEmitindo] = useState(false);
  const emitirRelatorio = useCallback(async () => {
    setEmitindo(true);
    try {
      setRelatorio(await apiClient.relatorioDaUnidade(token));
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        onSair();
        return;
      }
      if (e instanceof ApiError && e.status === 409) {
        setRelIndisponivel(true); // ainda não validado → mensagem amigável
        return;
      }
      notificar("Falha ao emitir o relatório.");
    } finally {
      setEmitindo(false);
    }
  }, [token, onSair, notificar]);

  const carregar = useCallback(
    async (t: string) => {
      setErro(null);
      setCarregando(true);
      try {
        setPlanos(await apiClient.planosDaUnidade(t));
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          onSair(); // sessão expirada → volta ao login principal
          return;
        }
        setErro(e instanceof ApiError ? e.message : "Falha ao carregar os planos");
      } finally {
        setCarregando(false);
      }
    },
    [onSair],
  );

  useEffect(() => {
    void carregar(token);
  }, [token, carregar]);

  const aoSalvarItem = (atualizado: PlanoItem) => {
    setPlanos(
      (prev) =>
        prev?.map((p) =>
          p.avaliacaoId === atualizado.avaliacaoId
            ? { ...p, itens: p.itens.map((i) => (i.id === atualizado.id ? atualizado : i)) }
            : p,
        ) ?? null,
    );
  };

  const totais = useMemo(() => totaisDeItens(planos?.flatMap((p) => p.itens) ?? []), [planos]);
  const unidadeNome = planos?.[0] ? (NOME_UNIDADE.get(planos[0].unidadeId) ?? planos[0].unidadeId) : "";
  const temItens = !!planos && totais.total > 0;

  return (
    <div className="pl-portal">
      <header className="pl-topo">
        <div className="pl-topo-in">
          <div>
            <div className="pl-topo-sub">Plano de Ação</div>
            <h1 className="pl-topo-titulo">{unidadeNome || nome || "Minha unidade"}</h1>
            {(nome || cargo) && (
              <div className="pl-topo-quem">
                {[nome, cargo ? nomeCargo(cargo) : ""].filter(Boolean).join(" · ")}
              </div>
            )}
          </div>
          <div className="pl-topo-acoes">
            <button type="button" className="pl-emitir" onClick={emitirRelatorio} disabled={emitindo}>
              {emitindo ? "Gerando…" : "📄 Emitir relatório (PDF)"}
            </button>
            <button type="button" className="pl-sair" onClick={onSair}>
              Sair
            </button>
          </div>
        </div>
      </header>

      {relatorio && <EmitirRelatorioOverlay dto={relatorio} onFechar={() => setRelatorio(null)} />}

      {relIndisponivel && (
        <div className="ri-overlay">
          <div className="ri-toolbar no-print">
            <button type="button" className="m-btn" onClick={() => setRelIndisponivel(false)}>
              Fechar
            </button>
          </div>
          <RelatorioIndisponivel contexto="unidade" />
        </div>
      )}

      <div className="pl-shell">
        {/* Barra lateral de contexto — orienta e tranquiliza o usuário da unidade.
            NENHUM índice/nota de maturidade é exibido aqui (fora do escopo do papel). */}
        <aside className={`pl-side${sobreAberto ? " aberto" : ""}`}>
          <button type="button" className="pl-side-toggle" onClick={() => setSobreAberto((v) => !v)}>
            Sobre o Plano de Ação · como funciona
          </button>
          <div className="pl-side-cards">
            <div className="pl-side-card pl-side-brand">
              <div className="pl-brand-row">
                <Logo size={36} />
                <div>
                  <div className="mark">CAINT</div>
                  <div className="sub">Comissão de Avaliação Interna da Maturidade · EMSERH</div>
                </div>
              </div>
            </div>

            <div className="pl-side-card">
              <div className="pl-side-h">Sua unidade</div>
              <div className="pl-ident-nome">{unidadeNome || "Minha unidade"}</div>
              {nome && (
                <div className="pl-ident-row">
                  <span>Responsável</span> {nome}
                </div>
              )}
              {cargo && (
                <div className="pl-ident-row">
                  <span>Papel</span> {nomeCargo(cargo)}
                </div>
              )}
              <div className="pl-ident-row">
                <span>Setores</span> Todos os setores
              </div>
            </div>

            <div className="pl-side-card">
              <div className="pl-side-h">Como funciona</div>
              <ol className="pl-fluxo">
                <li>
                  <span className="pl-fluxo-n">1</span>
                  <div>
                    <b>Você comprova</b>
                    <p>Anexe uma foto ou PDF que mostre o que foi feito em cada ponto.</p>
                  </div>
                </li>
                <li>
                  <span className="pl-fluxo-n">2</span>
                  <div>
                    <b>O CAINT revisa</b>
                    <p>A equipe confere a evidência enviada.</p>
                  </div>
                </li>
                <li>
                  <span className="pl-fluxo-n">3</span>
                  <div>
                    <b>Ponto encerrado</b>
                    <p>Aprovado, o item é concluído. Se faltar algo, você recebe o motivo e reenvia.</p>
                  </div>
                </li>
              </ol>
            </div>

            <div className="pl-side-card">
              <div className="pl-side-h">O que cada situação significa</div>
              <ul className="pl-legenda">
                <li>
                  <span className="pl-dot pendente" />
                  <div>
                    <b>Pendente</b> — ainda sem evidência anexada.
                  </div>
                </li>
                <li>
                  <span className="pl-dot andamento" />
                  <div>
                    <b>Em andamento</b> — enviado, aguardando a revisão.
                  </div>
                </li>
                <li>
                  <span className="pl-dot concluido" />
                  <div>
                    <b>Concluído</b> — aprovado pelo time CAINT.
                  </div>
                </li>
                <li>
                  <span className="pl-dot rejeitado" />
                  <div>
                    <b>Rejeitado</b> — precisa de ajuste; o motivo aparece no item.
                  </div>
                </li>
              </ul>
            </div>

            <div className="pl-side-card pl-side-note">
              <div className="pl-side-h">Prazos</div>
              <p>
                Cada ação pode ter prazo de até 3 meses. Você pode salvar e voltar quando quiser — nada se
                perde.
              </p>
            </div>

            <div className="pl-side-card">
              <div className="pl-side-h">Precisa de ajuda?</div>
              <p>Em caso de dúvida sobre algum ponto, fale com o time CAINT pelo canal da sua unidade.</p>
            </div>
          </div>
        </aside>

        <div className="pl-progresso-wrap">{temItens && <BarraProgresso totais={totais} />}</div>

        <div className="pl-main">
          {carregando && !planos && (
            <div className="pl-carregando">
              <span className="pl-spinner" aria-hidden="true" />
              Carregando seus planos…
            </div>
          )}

          {erro && (
            <p role="alert" className="pl-erro" style={{ margin: "0 0 16px" }}>
              {erro}
            </p>
          )}

          {planos && totais.total === 0 && !carregando && (
            <div className="pl-vazio">
              <div className="pl-vazio-icon">✓</div>
              <h2>Nenhum plano pendente</h2>
              <p>Quando a avaliação apontar pontos a melhorar, eles aparecerão aqui para você responder.</p>
            </div>
          )}

          {/* A escala explicada uma vez, recolhível: o card mostra o NÍVEL do item,
              e sem a régua a unidade não sabe o que "nível 1" significa. */}
          {(planos?.length ?? 0) > 0 && <LegendaMaturidade />}
          {planos?.map((plano) => (
            <section key={plano.avaliacaoId} className="pl-plano">
              <SecoesPorSetor
                itens={plano.itens}
                token={token}
                onSalvo={aoSalvarItem}
                onNotificar={notificar}
              />
            </section>
          ))}
        </div>
      </div>

      {aviso && <output className="pl-toast">{aviso}</output>}
    </div>
  );
}
